import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HiArrowLeft, HiDownload, HiExternalLink,
  HiEye, HiSparkles
} from 'react-icons/hi';
import { FaFilePdf } from 'react-icons/fa';
import './CVPage.css';

const CV_URL = '/sudip_majkoti_cv.pdf';
const PDF_PAGE_COUNT = 3;
const PDF_PAGE_ASPECT_RATIO = 792 / 612; // US Letter height / width

const CVPage = () => {
  const iframeRef = useRef(null);
  const wrapperRef = useRef(null);
  const [embedHeight, setEmbedHeight] = useState(null);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    const prevTitle = document.title;
    document.title = 'Sudip Majkoti | Curriculum Vitae';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (wrapperRef.current) {
        const width = wrapperRef.current.clientWidth;
        if (width > 0) {
          // Calculate full height for all 3 pages at FitH zoom + viewer spacing buffer
          const fullDocHeight = Math.ceil(
            width * PDF_PAGE_ASPECT_RATIO * PDF_PAGE_COUNT + 120
          );
          setEmbedHeight(fullDocHeight);
        }
      }
    };

    updateHeight();

    let observer;
    if (typeof ResizeObserver !== 'undefined' && wrapperRef.current) {
      observer = new ResizeObserver(updateHeight);
      observer.observe(wrapperRef.current);
    }
    window.addEventListener('resize', updateHeight);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <div className="cv-page">
      {/* Top Header Navigation */}
      <header className="cv-page__nav">
        <div className="container cv-page__nav-inner">
          <div className="cv-page__nav-left">
            <Link to="/" className="cv-page__back-btn" title="Return to Portfolio">
              <HiArrowLeft />
              <span>Back to Portfolio</span>
            </Link>

            <span className="cv-page__nav-divider">/</span>

            <Link to="/" className="cv-page__brand">
              <span className="cv-page__brand-bracket">[</span>
              <span className="cv-page__brand-name">Sudip Majkoti</span>
              <span className="cv-page__brand-dot">.</span>
              <span className="cv-page__brand-bracket">]</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="cv-page__main">
        <div className="container">
          <motion.div
            className="cv-page__header"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="cv-page__badge-row">
              <span className="cv-page__badge">
                <HiSparkles />
                <span>OFFICIAL CURRICULUM VITAE</span>
              </span>
              <span className="cv-page__badge-meta">
                PDF Document &bull; Updated 2026 &bull; AI Engineer
              </span>
            </div>

            <h1 className="cv-page__title">Sudip Majkoti &mdash; Resume</h1>
            <p className="cv-page__desc">
              Full-resolution document preview. Includes complete professional background, engineering projects, AI agent architectures, and technical competencies.
            </p>
          </motion.div>

          {/* Big High-Resolution Preview Frame */}
          <motion.div
            className="cv-page__viewer-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Terminal Window Header Bar */}
            <div className="cv-page__viewer-bar">
              <div className="cv-page__window-dots">
                <span className="cv-dot cv-dot--red" />
                <span className="cv-dot cv-dot--yellow" />
                <span className="cv-dot cv-dot--green" />
              </div>

              <div className="cv-page__viewer-filename">
                <FaFilePdf className="cv-page__file-icon" />
                <span>sudip_majkoti_cv.pdf</span>
                <span className="cv-page__file-tag">[ 100% VECTOR &bull; FIT TO WIDTH ]</span>
              </div>

              <div className="cv-page__viewer-controls">
                <a
                  href={CV_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cv-page__control-btn"
                  title="Expand to Full Tab"
                >
                  <HiEye />
                  <span>Full View</span>
                </a>
                <a
                  href={CV_URL}
                  download="Sudip_Majkoti_CV.pdf"
                  className="cv-page__control-btn cv-page__control-btn--highlight"
                  title="Download File"
                >
                  <HiDownload />
                  <span>Download</span>
                </a>
              </div>
            </div>

            {/* Expansive Document Iframe */}
            <div
              ref={wrapperRef}
              className="cv-page__embed-wrapper"
              style={embedHeight ? { height: `${embedHeight}px` } : undefined}
            >
              <iframe
                ref={iframeRef}
                src={`${CV_URL}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                title="Sudip Majkoti Curriculum Vitae Full Preview"
                className="cv-page__embed-frame"
                scrolling="no"
              />
            </div>

            {/* Mobile Helper Strip */}
            <div className="cv-page__mobile-helper">
              <p>
                Viewing on mobile? For responsive pinch-to-zoom and multi-page view:
              </p>
              <div className="cv-page__mobile-actions">
                <a
                  href={CV_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline btn-sm"
                >
                  <HiExternalLink />
                  <span>Open in Fullscreen</span>
                </a>
                <a
                  href={CV_URL}
                  download="Sudip_Majkoti_CV.pdf"
                  className="btn btn-primary btn-sm"
                >
                  <HiDownload />
                  <span>Download PDF</span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Mini Footer */}
      <footer className="cv-page__footer">
        <div className="container cv-page__footer-inner">
          <p>
            &copy; {new Date().getFullYear()} Sudip Majkoti &bull; AI Engineer &bull; Nepal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CVPage;
